import _ from 'lodash';
import axios from 'axios';
import moment from 'moment-timezone';
import {withRouter} from 'react-router-dom';

import {AggregateViewReceive} from './AggregateViewReceive';
import APIGatewayClientProvider from '../../provider/APIGatewayClientProvider';
import MetricAggregatesReportRequestInput from '../../pojos/MetricAggregatesReportRequestInput';
import MetricAggregationAction from '../../pojos/MetricAggregationAction';
import {
    createCardinalityMetricAggregation,
    createGroupByDimension,
    groupWorkPoolsByMetricField
} from '../../pojos/MetricAggregations';
import {
    AGGREGATE_OPTIONS_ENUM,
    VIEW_NAME_TO_TIME_RANGE_PROPERTY_MAP
} from '../../pojos/AggregateOptionParameters';
import {
    DATE_RANGE_ENUM,
    DEFAULT_AXIS_VALUES_ENUM,
    SHIPMENT_TYPES_ENUM
} from '../../pojos/Dimensions';
import {
    BUCKET_SIZES_IN_MINUTES,
    WORK_VIEW_DEFAULT_BUCKET_SIZE
} from '../../pojos/Buckets';
import {
    EXPECTED_STOW_DATE_FILTER_NAME,
    ExpectedStowDateFilter,
    TRAILER_DOCKING_TIME_FILTER_NAME,
    TrailerDockingTimeFilter,
    WorkPoolFilter
} from '../../pojos/RodeoFilters';
import {QueryStringUtilities} from '../../util/QueryStringUtilities';
import {DateUtilities} from '../../util/DateUtilities';
import {RouteUtilities as AppRouter} from '../../router/RouteDefinitions';
import Notification from '../../notification/Notification';

const RELOAD_AGGREGATE_PARAMETER_NAMES = [
    AGGREGATE_OPTIONS_ENUM.X_AXIS,
    AGGREGATE_OPTIONS_ENUM.Y_AXIS,
    AGGREGATE_OPTIONS_ENUM.Z_AXIS,
    AGGREGATE_OPTIONS_ENUM.BUCKET_SIZE,
    AGGREGATE_OPTIONS_ENUM.EXPECTED_STOW_DATE_RANGE_OPTION,
    AGGREGATE_OPTIONS_ENUM.TRAILER_DOCKING_TIME_RANGE_OPTION,
    AGGREGATE_OPTIONS_ENUM.SHIPMENT_TYPE
];

class MetricAggregateViewReceive extends AggregateViewReceive {

    componentDidUpdate(prevProps, prevState) {
        if (this.props.location.search !== prevProps.location.search) {
            this.parseQueryStringState();
            return;
        }

        const aggregateParameterChanged = _.some(RELOAD_AGGREGATE_PARAMETER_NAMES,
            parameterName => this.state.aggregateParameters[parameterName] !== prevState.aggregateParameters[parameterName]);
        if (!_.isEqual(this.state.rodeoFilters, prevState.rodeoFilters) || aggregateParameterChanged) {
            this.loadAggregateReport();
        }
    }

    createAggregateParameters(parsedQueryStrings) {
        const aggregateParameters = super.createAggregateParameters(parsedQueryStrings);
        aggregateParameters.bucketSize = MetricAggregateViewReceive.getParameterValueFromQueryStringsOrDefault(
            parsedQueryStrings,
            AGGREGATE_OPTIONS_ENUM.BUCKET_SIZE,
            WORK_VIEW_DEFAULT_BUCKET_SIZE);
        // Work-view-specific date-range defaults: the Post Receive work view defaults the
        // Expected Stow Date range to "All", and the Pre Receive work view defaults the
        // Trailer Docking Time range to +/- 7 days. Each work view only uses its own range
        // option, so setting both defaults here is safe.
        aggregateParameters[AGGREGATE_OPTIONS_ENUM.EXPECTED_STOW_DATE_RANGE_OPTION] =
            MetricAggregateViewReceive.getParameterValueFromQueryStringsOrDefault(
                parsedQueryStrings,
                AGGREGATE_OPTIONS_ENUM.EXPECTED_STOW_DATE_RANGE_OPTION,
                DATE_RANGE_ENUM.ALL);
        aggregateParameters[AGGREGATE_OPTIONS_ENUM.TRAILER_DOCKING_TIME_RANGE_OPTION] =
            MetricAggregateViewReceive.getParameterValueFromQueryStringsOrDefault(
                parsedQueryStrings,
                AGGREGATE_OPTIONS_ENUM.TRAILER_DOCKING_TIME_RANGE_OPTION,
                DATE_RANGE_ENUM.PLUS_MINUS_SEVEN_DAYS);
        return aggregateParameters;
    }

    aggregateReport(originalResponseInJSONArray = null) {
        const responses = _.isNil(originalResponseInJSONArray) ? this.state.originalResponseInJSONArray : originalResponseInJSONArray;
        const aggregationAction = new MetricAggregationAction(
            this.props.viewName,
            this.state.aggregateParameters,
            this.state.warehouseTimezone,
            this.state.rodeoFilters);
        let aggregatedResponseObject = null;
        responses.forEach(response => {
            aggregatedResponseObject = aggregationAction.aggregate(response, aggregatedResponseObject);
        });
        return this.updatePreviousAggregatedResponseObjectForWorkPoolDimension(aggregatedResponseObject);
    }

    loadAggregateReport() {
        this.cancelTokenSource.cancel('Cancelling previous running MetricAggregateView ajax requests');
        this.cancelTokenSource = APIGatewayClientProvider.createNewCancelTokenSource();
        this.setState({isLoaded: false});

        const parsedQueryStrings = QueryStringUtilities.parseQueryStrings(this.props.location.search);
        const rodeoFilters = this.createRodeoFiltersFromQueryStrings(parsedQueryStrings);
        this.addTimeRangeUpperBoundFilter(rodeoFilters);

        let requests;
        try {
            requests = this.createMetricAggregatesReportRequests(rodeoFilters);
        } catch (error) {
            this.handleMetricAggregateLoadError(error);
            return;
        }

        Promise.all(requests)
            .then(responses => responses.map(response => response.data))
            .then(originalResponseInJSONArray => {
                const aggregatedResponseObject = this.aggregateReport(originalResponseInJSONArray);
                this.setState({
                    aggregatedResponseObject,
                    originalResponseInJSONArray,
                    isLoaded: true
                });
            })
            .catch(error => {
                if (!axios.isCancel(error)) {
                    this.handleMetricAggregateLoadError(error);
                }
            });
    }

    createMetricAggregatesReportRequests(rodeoFilters) {
        const workPools = rodeoFilters.workPoolFilter?.values || [];
        const shipmentTypes = this.state.aggregateParameters.shipmentType === SHIPMENT_TYPES_ENUM.ALL
            ? [SHIPMENT_TYPES_ENUM.TRANSSHIPMENT, SHIPMENT_TYPES_ENUM.VENDOR_RECEIVE]
            : [this.state.aggregateParameters.shipmentType];
        const groupByDimensions = this.createMetricGroupByDimensions();
        const requests = [];

        for (const shipmentType of shipmentTypes) {
            const workPoolsByMetricField = groupWorkPoolsByMetricField(workPools, shipmentType);

            for (const [metricFieldName, groupedWorkPools] of workPoolsByMetricField.entries()) {
                const groupedRodeoFilters = {
                    ...rodeoFilters,
                    workPoolFilter: new WorkPoolFilter().withValues(groupedWorkPools)
                };
                const metricAggregationRequest = {
                    groupByDimensions,
                    metricAggregations: [createCardinalityMetricAggregation(metricFieldName)]
                };
                const requestInput = new MetricAggregatesReportRequestInput.Builder()
                    .withWarehouseId(this.props.warehouseId)
                    .withShipmentTypes([shipmentType])
                    .withFilterList(groupedRodeoFilters)
                    .withMetricAggregationRequest(metricAggregationRequest)
                    .build();

                requests.push(this.context.client.getMetricAggregatesReport(requestInput, this.cancelTokenSource.token));
            }
        }

        return requests;
    }

    createMetricGroupByDimensions() {
        const bucketSizeInMinutes = BUCKET_SIZES_IN_MINUTES.get(this.state.aggregateParameters.bucketSize);
        if (_.isNil(bucketSizeInMinutes)) {
            throw new Error(`Unsupported bucket size ${this.state.aggregateParameters.bucketSize}`);
        }

        const bucketReferencePoint = moment().tz(this.state.warehouseTimezone).startOf('day').unix();

        const dimensions = {
            ...createGroupByDimension(this.state.aggregateParameters.xAxis, bucketSizeInMinutes, bucketReferencePoint),
            ...createGroupByDimension(this.state.aggregateParameters.yAxis)
        };
        if (this.state.aggregateParameters.zAxis !== DEFAULT_AXIS_VALUES_ENUM.Z_DEFAULT) {
            Object.assign(dimensions, createGroupByDimension(this.state.aggregateParameters.zAxis));
        }
        return dimensions;
    }

    addTimeRangeUpperBoundFilter(rodeoFilters) {
        const rangeProperty = VIEW_NAME_TO_TIME_RANGE_PROPERTY_MAP.get(this.props.viewName);
        const selectedRange = this.state.aggregateParameters[rangeProperty];
        if (selectedRange === DATE_RANGE_ENUM.ALL) {
            return;
        }

        const timeInterval = DateUtilities.getStartAndEndMillisFromSelectedRangeName(
            selectedRange,
            this.state.warehouseTimezone);
        if (_.isNil(timeInterval)) {
            return;
        }

        if (AppRouter.isPreReceiveAggregateView(this.props.viewName)) {
            rodeoFilters[TRAILER_DOCKING_TIME_FILTER_NAME] = new TrailerDockingTimeFilter()
                .withFrom(0)
                .withTo(timeInterval.end);
        } else {
            rodeoFilters[EXPECTED_STOW_DATE_FILTER_NAME] = new ExpectedStowDateFilter()
                .withFrom(0)
                .withTo(timeInterval.end);
        }
    }

    handleMetricAggregateLoadError(error) {
        const errorMessage = APIGatewayClientProvider.parseError(error);
        this.notificationRef.current.addNotification(new Notification.Builder('metricAggregateViewDataLoadError')
            .withType(Notification.TYPES.ERROR)
            .withMessage(`Error retrieving metric aggregations: ${errorMessage}`)
            .build());
        this.setState({
            aggregatedResponseObject: null,
            originalResponseInJSONArray: [],
            isLoaded: true
        });
    }
}

export {MetricAggregateViewReceive};
export default withRouter(MetricAggregateViewReceive);
